import {formEncodedApi} from 'nutshell-core/api';
import {mapEntityToDisplayOption} from 'nutshell-core/utils';

import type {GetTeams_teams as Team} from 'nutshell-graphql-types';

const MAX_SERVER_RESULTS = 25;
const MAX_LOCAL_RESULTS = 25;
const COLLECTION_NAMES = ['accounts', 'contacts', 'Leads', 'Users'];

/**
 * Get locally cached entities from Nut.managedCollections
 * This matches the behavior from public/include/js/modules/core/mentions.js
 */
function getLocalEntities(query: string, teams?: Team[] | null): Array<any> {
    // @ts-expect-error
    const localResults = [];
    const queryLower = query.toLowerCase();

    // Get entities from Nut.managedCollections (same as old implementation)
    COLLECTION_NAMES.forEach((collectionName) => {
        if (localResults.length >= MAX_LOCAL_RESULTS) return;

        const collection =
            // @ts-expect-error
            window.Nut &&
            // @ts-expect-error
            window.Nut.managedCollections &&
            // @ts-expect-error
            window.Nut.managedCollections[collectionName];

        if (collection && collection.models) {
            // @ts-expect-error
            collection.models.forEach((entity) => {
                if (localResults.length >= MAX_LOCAL_RESULTS) return;

                const name = entity.get('name');
                const entityType = entity.get('type');

                if (name && (!query || name.toLowerCase().indexOf(queryLower) > -1)) {
                    localResults.push({
                        id: entity.id,
                        type: entityType,
                        value: name,
                        description: '',
                        // Include any additional fields needed
                        initials: entity.get('initials'),
                        avatarUrl: entity.get('avatarUrl'),
                    });
                }
            });
        }
    });

    // Filter teams and add to local results
    const filteredTeams = teams
        ? teams
              .filter((team) => {
                  if (!query) return true;

                  return team.name.toLowerCase().indexOf(queryLower) > -1;
              })
              .map((team) => ({...team, value: team.name, description: '', type: 'team'}))
        : [];

    // @ts-expect-error
    return [...localResults, ...filteredTeams].slice(0, MAX_LOCAL_RESULTS);
}

export function getEntities(query: string, teams?: Team[] | null): Promise<any> {
    // Get locally cached entities (users, contacts, accounts, leads, teams)
    const localEntities = getLocalEntities(query, teams);

    const data = {
        rows: MAX_SERVER_RESULTS,
        fields: [
            {
                name: 'name',
                value: query,
            },
        ],
    } as const;

    return (
        formEncodedApi
            .post('/solr/find-mentionable-entities.json', {
                ...data,
            })
            // @ts-expect-error
            .then((response) => response.json())
            .then((response: {results: any[]}) => {
                const results: any[] = response.results ? response.results : [];

                const serverResults = results
                    .map((entity) => {
                        return mapEntityToDisplayOption(entity);
                    })
                    .filter((entity) => entity.value);

                // Merge server results with local entities, removing duplicates
                // Server results take precedence (they have more up-to-date data)
                const serverIds = new Set(serverResults.map((e) => e.id));

                // Keep server results and only add local entities not in server
                // results
                const uniqueLocalResults = localEntities.filter(
                    (entity) => !serverIds.has(entity.id)
                );

                return [...serverResults, ...uniqueLocalResults];
            })
            .catch(() => {
                // If server request fails, fall back to just showing local entities
                return localEntities;
            })
    );
}
